import axios from "axios";
import { buildApiUrl } from "./api";

const BASE = "/api/system";

function get(path, params = {}) {
    return axios.get(buildApiUrl(`${BASE}${path}`), { params, withCredentials: true });
}

function post(path, data = {}) {
    return axios.post(buildApiUrl(`${BASE}${path}`), data, { withCredentials: true });
}

// ── Validation listing ──────────────────────────────────────────────────────

export async function fetchValidationList({
    page = 1,
    page_size = 25,
    strategy,
    governance_state,
    date_from,
    date_to,
    max_overfit_score,
    max_ruin_probability,
    min_ci_lower,
    sort_by = "validation_timestamp",
    sort_dir = "DESC",
} = {}) {
    const params = { page, page_size, sort_by, sort_dir };
    if (strategy) params.strategy = strategy;
    if (governance_state) params.governance_state = governance_state;
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    if (max_overfit_score != null) params.max_overfit_score = max_overfit_score;
    if (max_ruin_probability != null) params.max_ruin_probability = max_ruin_probability;
    if (min_ci_lower != null) params.min_ci_lower = min_ci_lower;
    const { data } = await get("/strategy-validations/list", params);
    return data;
}

// ── Validation detail ───────────────────────────────────────────────────────

export async function fetchValidationDetail(runId) {
    const { data } = await get(`/strategy-validations/${runId}`);
    return data;
}

// ── Validation dashboard ────────────────────────────────────────────────────

export async function fetchValidationDashboard(strategies) {
    const params = strategies ? { strategies: strategies.join(",") } : {};
    const { data } = await get("/strategy-validations/dashboard", params);
    return data;
}

// ── Trend analytics ─────────────────────────────────────────────────────────

export async function fetchStrategyTrend(strategy, limit = 20) {
    const { data } = await get(`/strategy-validations/trend/${strategy}`, { limit });
    return data;
}

export async function fetchTrendAnalytics(strategy) {
    const { data } = await get("/strategy-validations/trends", { strategy });
    return data;
}

// ── Chart data ──────────────────────────────────────────────────────────────

export async function fetchChartData(strategy, limit = 25) {
    const { data } = await get(`/strategy-validations/chart-data/${strategy}`, { limit });
    return data;
}

export async function fetchHealthTimeline(strategy, limit = 50) {
    const { data } = await get(`/strategy-validations/health-timeline/${strategy}`, { limit });
    return data;
}

// ── Health scores ───────────────────────────────────────────────────────────

export async function fetchHealthScores(strategy) {
    const { data } = await get("/strategy-validations/health", { strategy });
    return data;
}

// ── Drift analysis ──────────────────────────────────────────────────────────

export async function fetchDriftAnalysis(strategy) {
    const { data } = await get("/strategy-validations/drift", { strategy });
    return data;
}

// ── Cross-strategy summary ──────────────────────────────────────────────────

export async function fetchCrossStrategySummary(strategies) {
    const params = strategies ? { strategies: strategies.join(",") } : {};
    const { data } = await get("/strategy-validations/summary", params);
    return data;
}

// ── Governance heatmap ──────────────────────────────────────────────────────

export async function fetchGovernanceHeatmap(strategies) {
    const params = strategies ? { strategies: strategies.join(",") } : {};
    const { data } = await get("/strategy-validations/heatmap", params);
    return data;
}

// ── Research feed ───────────────────────────────────────────────────────────

export async function fetchResearchFeed(limit = 50, days = 90) {
    const { data } = await get("/strategy-validations/research-feed", { limit, days });
    return data;
}

// ── Comparison ──────────────────────────────────────────────────────────────

export async function compareRuns(runA, runB) {
    const { data } = await get("/strategy-validations/compare", { run_a: runA, run_b: runB });
    return data;
}

export async function compareLatestVsPrevious(strategy) {
    const { data } = await get("/strategy-validations/compare/latest-vs-previous", { strategy });
    return data;
}

// ── Special queries ─────────────────────────────────────────────────────────

export async function fetchDegradedStrategies(limit = 50) {
    const { data } = await get("/strategy-validations/degraded", { limit });
    return data;
}

export async function fetchLatestValidation(strategy) {
    const { data } = await get("/strategy-validations/latest", { strategy });
    return data;
}

// ── Monitoring ──────────────────────────────────────────────────────────────

export async function fetchMonitoringAlerts({ strategy, severity, include_resolved, limit = 100 } = {}) {
    const params = { limit };
    if (strategy) params.strategy = strategy;
    if (severity) params.severity = severity;
    if (include_resolved) params.include_resolved = "true";
    const { data } = await get("/strategy-monitoring/alerts", params);
    return data;
}

export async function fetchMonitoringWatchlist(strategies) {
    const params = strategies ? { strategies: strategies.join(",") } : {};
    const { data } = await get("/strategy-monitoring/watchlist", params);
    return data;
}

export async function fetchMonitoringHistory(strategy, limit = 50) {
    const { data } = await get(`/strategy-monitoring/history/${strategy}`, { limit });
    return data;
}

export async function fetchMonitoringSummary(strategies) {
    const params = strategies ? { strategies: strategies.join(",") } : {};
    const { data } = await get("/strategy-monitoring/summary", params);
    return data;
}

export async function resolveAlert(alertId) {
    const { data } = await post(`/strategy-monitoring/alerts/${alertId}/resolve`);
    return data;
}

// ── Market data realism ─────────────────────────────────────────────────────

export async function fetchValidationRealism(strategy) {
    const params = strategy ? { strategy } : {};
    const { data } = await get("/market-data/validation-realism", params);
    return data;
}

// ── Deployment lifecycle ───────────────────────────────────────────────────

export async function fetchLifecycleSummary(strategies) {
    const params = strategies ? { strategies: strategies.join(",") } : {};
    const { data } = await get("/strategy-lifecycle/summary", params);
    return data;
}

export async function fetchLifecycleStatus(strategy) {
    const { data } = await get("/strategy-lifecycle/status", { strategy });
    return data;
}

export async function fetchLifecycleHistory(strategy, limit = 50) {
    const { data } = await get("/strategy-lifecycle/history", { strategy, limit });
    return data;
}
